import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PenLine, Search, FileText, ShieldCheck, Users } from "lucide-react";
import api from "../lib/api";
import { ui } from "../lib/ui";
import ReportCard from "../components/ReportCard";
import HotTopics from "../components/HotTopics";

function StatItem({ value, label }) {
  return (
    <div className="text-center px-4">
      <div className="font-serif text-3xl sm:text-4xl font-bold text-[#8B1A1A]">{value}</div>
      <div className={ui.caption + " mt-1"}>{label}</div>
    </div>
  );
}

export default function Home() {
  const [latest, setLatest] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/reports/latest").then((r) => setLatest(r.data)).catch(() => {});
    api.get("/reports/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      {/* Hero */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 pt-14 pb-12 sm:pt-20 sm:pb-16">
        <div className="max-w-3xl fade-up">
          <span className={ui.pill + " mb-5"}>Crowdsourced · Community-moderated</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-[#8B1A1A] leading-tight tracking-tight">
            Report Your TEF
          </h1>
          <p className="mt-5 text-base sm:text-lg text-[#210201] leading-relaxed max-w-2xl">
            Just finished your TEF Canada exam? Share what came up: speaking topics, writing prompts, and how
            many comprehension questions matched the prediction plan you studied. Help the next candidate walk in prepared.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <Link to="/submit" className={ui.btnPrimary} data-testid="home-hero-submit-btn">
              <PenLine size={18} /> Report my exam
            </Link>
            <Link to="/archive" className={ui.btnSecondary} data-testid="home-hero-browse-btn">
              <Search size={18} /> Browse the archive
            </Link>
          </div>
        </div>
      </section>

      {/* Hot this month */}
      <div className="pt-2 pb-6">
        <HotTopics />
      </div>

      {/* Stats strip */}
      {stats && (
        <section className="bg-[#F5F0E8] border-y border-[#E8DCC8]">
          <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8 flex justify-around items-center flex-wrap gap-6">
            <StatItem value={stats.total ?? 0} label="Total reports" />
            <div className="h-10 w-px bg-[#E8DCC8] hidden sm:block" />
            <StatItem value={stats.this_month ?? 0} label="Reports this month" />
            <div className="h-10 w-px bg-[#E8DCC8] hidden sm:block" />
            <StatItem value={stats.avg_matched != null ? stats.avg_matched : "0"} label="Avg. predictions matched" />
          </div>
        </section>
      )}

      {/* Latest reports */}
      <section className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#8B1A1A]">Latest reports</h2>
          <Link to="/archive" className={ui.btnGhost}>View all →</Link>
        </div>
        {latest.length === 0 ? (
          <div className={ui.card + " p-10 text-center text-[#5A4441]"}>
            No approved reports yet. Be the first to <Link to="/submit" className="text-[#8B1A1A] underline">report your exam</Link>.
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2" data-testid="home-latest-reports-list">
            {latest.map((r) => (
              <ReportCard key={r.id} report={r} />
            ))}
          </div>
        )}
      </section>

      {/* Why share */}
      <section className="bg-[#F5F0E8] border-t border-[#E8DCC8]">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-[#8B1A1A] mb-8">Why share your experience</h2>
          <div className="grid gap-6 sm:grid-cols-3">
            {[
              { icon: FileText, t: "Real, recent topics", d: "See what genuinely came up in Expression orale and écrite, city by city." },
              { icon: Users, t: "Built by candidates", d: "Every report comes from someone who just sat the exam, like you will." },
              { icon: ShieldCheck, t: "Moderated & clean", d: "We review each report, remove personal details, and keep it accurate." },
            ].map(({ icon: Icon, t, d }) => (
              <div key={t} className={ui.card + " p-6"}>
                <div className="w-11 h-11 rounded-xl bg-[#F3E5D8] flex items-center justify-center mb-4">
                  <Icon size={20} className="text-[#8B1A1A]" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-[#8B1A1A] mb-1.5">{t}</h3>
                <p className="text-sm text-[#5A4441] leading-relaxed">{d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
